import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment-timezone";
import "moment/locale/az";

import {
  csAdminUserDetail,
  csAdminApprove,
  csAdminReject,
  csAdminActivate,
  csAdminDeactivate,
  csAdminForceAddGroup,
  csAdminForceRemoveGroup,
  csAdminAdjustBalance,
} from "../../api/admin.api";

import { csMySubscription } from "../../api/subscription.api";
import { csMyPayments } from "../../api/payments.api";
import { csIsInWhatsappGroup } from "../../api/whatsapp.api";

import styles from "../../styles/adminUserDetail.module.css";

moment.locale("az");
const AZ_TZ = "Asia/Baku";

// 994551234567 -> 0551234567
function displayPhoneAz(phone) {
  const p = String(phone || "").replace(/\s+/g, "");
  if (!p) return "-";
  if (p.startsWith("994") && p.length >= 12) return "0" + p.slice(3);
  if (p.startsWith("+994") && p.length >= 13) return "0" + p.slice(4);
  return p;
}

function azVerifyStatusLabel(s) {
  const v = String(s || "").toUpperCase();
  switch (v) {
    case "REGISTERED": return "Qeydiyyat";
    case "PHONE_PENDING": return "Telefon gözləmədə";
    case "PHONE_VERIFIED": return "Telefon təsdiqləndi";
    case "DOCS_SUBMITTED": return "Sənədlər göndərilib";
    case "APPROVED": return "Təsdiqləndi";
    case "REJECTED": return "Rədd edildi";
    default: return v || "-";
  }
}

function verifyPillClass(vs) {
  const v = String(vs || "").toUpperCase();
  if (v === "APPROVED") return `${styles.pill} ${styles.pillOk}`;
  if (v === "REJECTED") return `${styles.pill} ${styles.pillBad}`;
  if (v === "DOCS_SUBMITTED" || v === "PHONE_VERIFIED") return `${styles.pill} ${styles.pillWarn}`;
  return styles.pill;
}

function pillOkBad(ok) {
  return ok ? `${styles.pill} ${styles.pillOk}` : `${styles.pill} ${styles.pillBad}`;
}

function fmtAzDateTime(v) {
  if (!v) return "-";
  const m = moment(v);
  if (!m.isValid()) return "-";
  return m.tz(AZ_TZ).format("DD MMMM YYYY HH:mm:ss");
}

function fmtAzDateOnly(v) {
  if (!v) return "-";
  const m = moment(v);
  if (!m.isValid()) return "-";
  return m.tz(AZ_TZ).format("DD MMMM YYYY");
}

function normalizeSubscription(subPayload) {
  const st = String(subPayload?.status || "").toUpperCase();
  const active = st === "ACTIVE";
  const plan = subPayload?.plan || "-";
  const start = subPayload?.currentPeriodStart || null;
  const end = subPayload?.currentPeriodEnd || null;
  return { active, status: st || "UNKNOWN", plan, start, end };
}

export default function UserDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [u, setU] = useState(null);
  const [sub, setSub] = useState(null);
  const [inGroup, setInGroup] = useState(null);

  const [payments, setPayments] = useState([]);
  const [showCount, setShowCount] = useState(5);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const phone = u?.phone || null;
  const phoneUi = useMemo(() => displayPhoneAz(phone), [phone]);

  const canApproveReject = useMemo(() => {
    const vs = String(u?.verifyStatus || "").toUpperCase();
    return vs === "DOCS_SUBMITTED";
  }, [u]);

  const accountActive = useMemo(() => u?.active !== false, [u]); // default true
  const accountLabel = accountActive ? "AKTİVDİR" : "DEAKTİVDİR";

  const subInfo = useMemo(() => {
    if (!sub) return { has: null, text: "-", plan: "-", end: "-" };
    const has = !!sub.active;
    return {
      has,
      text: has ? "VAR" : "YOXDUR",
      plan: sub.plan || "-",
      end: sub.currentPeriodEnd ? fmtAzDateOnly(sub.currentPeriodEnd) : "-",
    };
  }, [sub]);

  const loadAll = async () => {
    setMsg("");
    setLoading(true);
    setShowCount(5);

    try {
      const detailRes = await csAdminUserDetail(id);
      const payloadU = detailRes?.data?.data ?? detailRes?.data ?? detailRes ?? null;
      setU(payloadU);

      const userPhone = payloadU?.phone;
      if (userPhone) {
        // parallel
        const [subRes, groupRes, payRes] = await Promise.allSettled([
          csMySubscription(userPhone),
          csIsInWhatsappGroup(userPhone),
          csMyPayments(userPhone),
        ]);

        // subscription
        if (subRes.status === "fulfilled") {
          const sp = subRes.value?.data?.data ?? subRes.value?.data ?? subRes.value ?? null;
          setSub(sp);
        } else {
          setSub(null);
        }

        // group
        if (groupRes.status === "fulfilled") {
          const gp = groupRes.value?.data?.data ?? groupRes.value?.data ?? groupRes.value ?? groupRes.value;
          // gp = { inGroup: true/false } (sənin controller Map.of("inGroup", inGroup))
          setInGroup(Boolean(gp?.inGroup));
        } else {
          setInGroup(null);
        }

        // payments
        if (payRes.status === "fulfilled") {
          const pp = payRes.value?.data?.data ?? payRes.value?.data ?? payRes.value ?? [];
          const arr = Array.isArray(pp) ? pp : [];
          // ən yeni yuxarı: paidAt desc, yoxdursa createdAt desc, yoxdursa id desc
          const sorted = [...arr].sort((a, b) => {
            const at = a?.paidAt || a?.createdAt || null;
            const bt = b?.paidAt || b?.createdAt || null;
            const aT = at ? new Date(at).getTime() : null;
            const bT = bt ? new Date(bt).getTime() : null;
            if (Number.isFinite(aT) && Number.isFinite(bT)) return bT - aT;
            const aId = Number(a?.id || 0);
            const bId = Number(b?.id || 0);
            return bId - aId;
          });
          setPayments(sorted);
        } else {
          setPayments([]);
        }
      } else {
        setSub(null);
        setInGroup(null);
        setPayments([]);
      }
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Məlumatlar yüklənmədi.");
      setU(null);
      setSub(null);
      setInGroup(null);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (fn) => {
    if (busy) return;
    setMsg("");
    setBusy(true);
    try {
      await fn();
      await loadAll();
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Əməliyyat alınmadı.");
    } finally {
      setBusy(false);
    }
  };

  const visiblePayments = useMemo(() => payments.slice(0, showCount), [payments, showCount]);
  const canLoadMore = payments.length > showCount;

  if (loading && !u) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.loading}>Yüklənir...</div>
          {msg && <div className={styles.error}>{msg}</div>}
        </div>
      </div>
    );
  }

  if (!u) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.loading}>İstifadəçi tapılmadı.</div>
          {msg && <div className={styles.error}>{msg}</div>}
          <button className={styles.btn} onClick={() => nav(-1)} type="button">
            Geri
          </button>
        </div>
      </div>
    );
  }

  const verifyStatus = String(u?.verifyStatus || "").toUpperCase();
  const createdAt = u?.createdAt || u?.created || u?.createdDate || null;

  const balanceAz =
    u?.balanceInt ? (u.balanceInt / 100).toFixed(2) : "0.00";
  const invited = u?.invitedCount ?? 0;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.btnOutline} type="button" onClick={() => nav(-1)}>
              ← Geri
            </button>
            <div>
              <div className={styles.title}>Hesab məlumatları</div>
              <div className={styles.subtitle}>
                ID: <b>{u.id}</b>
                {msg ? <span className={styles.subtitleErr}> · {msg}</span> : null}
              </div>
            </div>
          </div>

          <button
            className={styles.btn}
            type="button"
            onClick={loadAll}
            disabled={loading || busy}
            title="Məlumatları yenilə"
          >
            {loading ? "Yüklənir..." : "Yenilə"}
          </button>
        </div>

        {/* TOP GRID */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Hesab məlumatları</div>

          <div className={styles.infoGrid}>
            <InfoRow label="Ad Soyad" value={`${u.firstName || ""} ${u.lastName || ""}`.trim() || "-"} />
            <InfoRow label="Telefon" value={phoneUi} />
            <InfoRow label="Şifrə" value={u.password || u.rawPassword || "-"} />

            <InfoRow label="Dəvət etdiyi adam sayı" value={invited} />
            <InfoRow label="Dəvət balansı" value={`${balanceAz} AZN`} />

            <div style={{ marginTop: 10 }}>
              <button
                className={styles.btn}
                onClick={() => {
                  const azn = prompt("Neçə AZN əlavə etmək istəyirsən? (məs: 5 və ya -2)");
                  if (!azn) return;

                  const amountInt = Math.round(Number(azn) * 100);
                  if (isNaN(amountInt)) return alert("Yanlış məbləğ");

                  csAdminAdjustBalance(id, {
                    amountInt,
                    note: "Admin düzəlişi"
                  }).then(() => loadAll());
                }}
              >
                Balansı dəyiş
              </button>
            </div>

            <InfoRow
              label="Yoxlama statusu"
              value={<span className={verifyPillClass(verifyStatus)}>{azVerifyStatusLabel(verifyStatus)}</span>}
            />

            <InfoRow
              label="Hesab statusu"
              value={<span className={pillOkBad(accountActive)}>{accountLabel}</span>}
            />

            <InfoRow
              label="Abunəlik"
              value={
                subInfo.has === null ? (
                  <span className={styles.muted}>Yoxlanılır...</span>
                ) : (
                  <div className={styles.inlineWrap}>
                    <span className={pillOkBad(subInfo.has)}>{subInfo.text}</span>
                    <span className={styles.mutedSmall}>
                      Plan: <b>{subInfo.plan}</b> · Bitmə: <b>{subInfo.end}</b>
                    </span>
                  </div>
                )
              }
            />

            <InfoRow label="Hesab yaradılma tarixi" value={createdAt ? fmtAzDateTime(createdAt) : "-"} />
          </div>
        </div>

        {/* APPROVE / REJECT only when DOCS_SUBMITTED */}
        {canApproveReject && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Sənədlər üzrə qərar</div>
            <div className={styles.actions}>
              <button
                className={`${styles.btn} ${styles.btnOk}`}
                type="button"
                disabled={busy}
                onClick={() => act(() => csAdminApprove(id, ""))}
              >
                {busy ? "Gözləyin..." : "Qəbul et"}
              </button>

              <button
                className={`${styles.btn} ${styles.btnBad}`}
                type="button"
                disabled={busy}
                onClick={() => act(() => csAdminReject(id, ""))}
              >
                {busy ? "Gözləyin..." : "Ləğv et"}
              </button>
            </div>
            <div className={styles.mutedSmall}>
              Bu düymələr yalnız status <b>Sənədlər göndərilib</b> olanda görünür.
            </div>
          </div>
        )}

        {/* DOCS */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Sənədlər</div>

          <div className={styles.docsGrid}>
            <DocCard title="Profil şəkli" url={u.profileImageUrl} />
            <DocCard title="Şəxsiyyət vəsiqəsi (ön)" url={u.idFrontUrl} />
            <DocCard title="Şəxsiyyət vəsiqəsi (arxa)" url={u.idBackUrl} />
          </div>
        </div>

        {/* EXTRA ACTIONS */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Əlavə əməliyyatlar</div>

          <div className={styles.actions}>
            {/* Block / Unblock by account status */}
            {accountActive ? (
              <button
                className={`${styles.btn} ${styles.btnBad}`}
                type="button"
                disabled={busy}
                onClick={() => act(() => csAdminDeactivate(id))}
              >
                Blokla
              </button>
            ) : (
              <button
                className={`${styles.btn} ${styles.btnOk}`}
                type="button"
                disabled={busy}
                onClick={() => act(() => csAdminActivate(id))}
              >
                Blokdan çıxart
              </button>
            )}

            {/* WhatsApp Group add/remove by membership */}
            {inGroup === null ? (
              <button className={styles.btnOutline} type="button" disabled>
                WhatsApp qrupu yoxlanılır...
              </button>
            ) : inGroup ? (
              <button
                className={styles.btnOutline}
                type="button"
                disabled={busy}
                onClick={() => act(() => csAdminForceRemoveGroup(id))}
              >
                WhatsApp qrupundan çıxart
              </button>
            ) : (
              <button
                className={styles.btnOutline}
                type="button"
                disabled={busy}
                onClick={() => act(() => csAdminForceAddGroup(id))}
              >
                WhatsApp qrupuna əlavə et
              </button>
            )}
          </div>

          <div className={styles.mutedSmall}>
            WhatsApp statusu:{" "}
            {inGroup === null ? (
              <b>yoxlanılır</b>
            ) : inGroup ? (
              <b>qrupdadır</b>
            ) : (
              <b>qrupda deyil</b>
            )}
          </div>
        </div>

        {/* PAYMENTS */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Ödənişlər</div>

          {payments.length === 0 ? (
            <div className={styles.muted}>Ödəniş tapılmadı.</div>
          ) : (
            <>
              <div className={styles.payList}>
                {visiblePayments.map((p) => {
                  const amount = p?.amountInt ? (p.amountInt / 100).toFixed(2) : (p?.amount ? String(p.amount) : "5.00");
                  const currency = p?.currency || "AZN";
                  const dt = p?.paidAt || p?.createdAt || null;
                  const status = String(p?.status || "-").toUpperCase();

                  const ok = status === "SUCCESS" || status === "PAID";
                  const bad = status === "FAILED";
                  const pillClass = ok ? `${styles.pill} ${styles.pillOk}` : bad ? `${styles.pill} ${styles.pillBad}` : `${styles.pill} ${styles.pillWarn}`;

                  const statusAz =
                    ok ? "Uğurlu" : bad ? "Uğursuz" : status === "INITIATED" ? "Gözləmədə" : status;

                  return (
                    <div key={p.id || `${p.createdAt}-${amount}`} className={styles.payRow}>
                      <div className={styles.payLeft}>
                        <div className={styles.payAmount}>
                          {amount} {currency}
                        </div>
                        <div className={styles.payDate}>{dt ? fmtAzDateTime(dt) : "-"}</div>
                      </div>
                      <span className={pillClass}>{statusAz}</span>
                    </div>
                  );
                })}
              </div>

              {canLoadMore && (
                <button
                  className={styles.btnOutline}
                  type="button"
                  onClick={() => setShowCount((c) => c + 5)}
                >
                  Daha çox göstər
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {

  return (
    <div className={styles.infoRow}>
      <div className={styles.key}>{label}</div>
      <div className={styles.val}>{value}</div>
    </div>
  );
}

function DocCard({ title, url }) {
  return (
    <div className={styles.docCard}>
      <div className={styles.docTitle}>{title}</div>

      <div className={styles.docThumb}>
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className={styles.docLink}>
            <img src={url} alt={title} className={styles.docImg} />
          </a>
        ) : (
          <div className={styles.docEmpty}>Yüklənməyib</div>
        )}
      </div>

      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className={styles.smallLink}>
          Böyük aç
        </a>
      ) : null}
    </div>
  );
}

